import { memo, useMemo } from 'react';
import { Box, Button, Chip, Divider, Paper, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InvestmentFinancialMetricsDTO } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { formatRate, formatRatio } from './metrics-copy';
import { cardSurface } from '../shared/surfaces';

interface TotalReturnCardProps {
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
  canEdit: boolean;
  onManageValuations: () => void;
}

const MISSING_TOTAL_ROE_HINT =
  'Hace falta conocer el equity inicial y tener movimientos registrados de caja o principal.';

type Driver = {
  key: string;
  label: string;
  help: string;
  amount: number | null;
  color: string;
};

export const TotalReturnCard = memo(
  ({ metrics, scaleAmount, canEdit, onManageValuations }: TotalReturnCardProps) => {
    const { equityMetrics, period } = metrics;
    const beginningEquity = equityMetrics.beginningEquity.value;

    const drivers = useMemo<Driver[]>(
      () => [
        {
          key: 'cash',
          label: 'Caja',
          help: 'Cashflow después de la cuota de la hipoteca: el dinero que realmente te ha llegado en el periodo.',
          amount: equityMetrics.periodCashflowAfterDebt.value,
          color: 'success.main',
        },
        {
          key: 'principal',
          label: 'Amortización',
          help: 'Principal devuelto al banco en el periodo. No lo cobras, pero aumenta tu equity.',
          amount: metrics.metrics.principalRepaid.value,
          color: 'primary.main',
        },
        {
          key: 'appreciation',
          label: 'Revalorización',
          help: 'Cambio del valor de mercado en el periodo, descontando el capex registrado para no contar una reforma como rentabilidad.',
          amount: equityMetrics.appreciation.value,
          color: 'secondary.main',
        },
      ],
      [
        equityMetrics.periodCashflowAfterDebt.value,
        equityMetrics.appreciation.value,
        metrics.metrics.principalRepaid.value,
      ]
    );

    const totalMagnitude = drivers.reduce((sum, driver) => sum + Math.abs(driver.amount ?? 0), 0);
    const canBreakDown = drivers.every((driver) => driver.amount !== null) && totalMagnitude > 0;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...cardSurface,
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Typography variant="overline" color="text.secondary" lineHeight={1.4}>
            Retorno total sobre tu equity
            <Tooltip
              title="Caja, amortización y revalorización sobre el equity que tenías al inicio del periodo. Es acumulado: no se anualiza."
              arrow
            >
              <InfoOutlinedIcon
                fontSize="inherit"
                sx={{ ml: 0.5, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }}
              />
            </Tooltip>
          </Typography>
          <Chip
            label={period.isYearToDate ? 'Acumulado YTD' : 'Periodo completo'}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.6875rem', flexShrink: 0 }}
          />
          {equityMetrics.appreciation.value === null ? (
            <Chip
              label="Sin revalorización"
              size="small"
              variant="outlined"
              color="warning"
              sx={{ height: 20, fontSize: '0.6875rem', flexShrink: 0 }}
            />
          ) : null}
        </Box>

        {equityMetrics.totalRoe.value === null ? (
          <Tooltip title={MISSING_TOTAL_ROE_HINT} arrow>
            <Typography
              variant="h3"
              fontWeight={700}
              color="text.disabled"
              sx={{
                fontSize: { xs: '2rem', md: '2.25rem' },
                mt: 0.5,
                cursor: 'help',
                width: 'fit-content',
              }}
            >
              —
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            variant="h3"
            fontWeight={700}
            color={equityMetrics.totalRoe.value >= 0 ? 'secondary.main' : 'error.main'}
            sx={{ fontSize: { xs: '2rem', md: '2.25rem' }, mt: 0.5 }}
          >
            {formatRatio(equityMetrics.totalRoe)}
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary">
          {beginningEquity === null
            ? 'Sobre el equity al inicio del periodo'
            : `Sobre ${formatCurrency(scaleAmount(beginningEquity))} de equity al inicio del periodo`}
        </Typography>
        {equityMetrics.appreciation.value === null ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Incluye caja y amortización; la revalorización queda fuera hasta registrar una
            valoración válida al cierre.
          </Typography>
        ) : null}

        {canBreakDown ? (
          <Box
            sx={{
              display: 'flex',
              height: 10,
              borderRadius: 5,
              overflow: 'hidden',
              mt: 2.5,
              gap: '2px',
            }}
            role="img"
            aria-label="Peso de cada fuente en el retorno total"
          >
            {drivers.map((driver) => (
              <Box
                key={driver.key}
                sx={{
                  width: `${(Math.abs(driver.amount ?? 0) / totalMagnitude) * 100}%`,
                  bgcolor: (driver.amount ?? 0) < 0 ? 'error.main' : driver.color,
                }}
              />
            ))}
          </Box>
        ) : null}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
          De dónde viene
        </Typography>

        <Stack spacing={1.25}>
          {drivers.map((driver) => {
            const share =
              driver.amount === null
                ? '—'
                : formatRate(
                    beginningEquity === null || beginningEquity <= 0
                      ? null
                      : driver.amount / beginningEquity
                  );

            return (
              <Box key={driver.key} display="flex" alignItems="baseline" gap={1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: (driver.amount ?? 0) < 0 ? 'error.main' : driver.color,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip title={driver.help} arrow>
                    <Typography variant="body2" noWrap sx={{ cursor: 'help' }}>
                      {driver.label}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                  >
                    {share}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  minWidth={64}
                  textAlign="right"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                  {share}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  minWidth={{ xs: 88, sm: 96 }}
                  textAlign="right"
                >
                  {driver.amount === null ? '—' : formatCurrency(scaleAmount(driver.amount))}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        {equityMetrics.totalRoe.value === null && canEdit ? (
          <Box sx={{ mt: 'auto', pt: 1.5 }}>
            <Button size="small" variant="text" sx={{ px: 0 }} onClick={onManageValuations}>
              Gestionar valoraciones
            </Button>
          </Box>
        ) : null}
      </Paper>
    );
  }
);

TotalReturnCard.displayName = 'TotalReturnCard';
